import Table from 'cli-table3';

export default class PrintableText {

    constructor(printableContent, charsPerLine = 32) {
        this.printableContent = printableContent;
        this.charsPerLine = charsPerLine
    }

    /**
     * Processes the printable content and generates formatted text.
     *
     * The method processes the printable content based on its type ('S', 'B', 'D', 'L', 'T')
     * and calls the corresponding process function.
     *
     * @returns {string[]} An array of formatted text lines. Each line is a string and corresponds to a printable entity.
     */
    process() {
        return this.printableContent.flatMap(c => {
            if(c.type === 'L') {
                const content = c.data[0].content[0] || ''
                const align = c.data[0].align || 'L'
                return this.processLine(content, align)
            } else if(c.type === 'S') {
                return this.processSpaceBetween(c.data[0].content)
            } else if(c.type === 'B') {
                return this.processSpaceOrBreak(c.data[0].content)
            } else if(c.type === 'D') {
                const delimiter = c.data[0].delimiter || ' '
                const content = c.data[0].content[0] || ''
                const align = c.data[0].align || 'L'
                return this.processDivider(delimiter, content, align)
            } else if(c.type === 'T') {
                const content = c.data || []
                const colNumber = c.colNumber || 2
                const colWidths = c.text?.colWidths || []
                return this.processTable(content, colNumber, colWidths)
            }
        })
    }

    /**
     * Process the input data and create a formatted table as string.
     *
     * @param {Array} data - The data to be inserted into the table. It should be an array of objects, each containing column data and options.
     * @param {Number} colNumber - The number of columns in the table.
     * @param {Array} [colWidths=[]] - An optional array specifying the width of each column. If not provided, column widths will be calculated automatically.
     * @return {String[]} Returns an array of string where each string is a row in the table.
     */
    processTable(data, colNumber, colWidths = []) {
        // Calculate the width of each column
        const availableCharsPerLine = this.charsPerLine - colNumber + 1
        if (colWidths?.length !== colNumber) {
            colWidths = Array(colNumber).fill(Math.floor(availableCharsPerLine / colNumber));
        }

        let totalFixedWidth = colWidths.filter(w => w !== null).reduce((a, b) => a + b, 0);
        let expandableColIndex = colWidths.indexOf(null);
        if (expandableColIndex !== -1) {
            colWidths[expandableColIndex] = availableCharsPerLine - totalFixedWidth;
        } else if(totalFixedWidth <= availableCharsPerLine) {
            colWidths[colWidths.length - 1] += availableCharsPerLine - totalFixedWidth;
        }

        const table = new Table({
            chars: {
                'top': '',
                'top-mid': '',
                'top-left': '',
                'top-right': '',
                'bottom': '',
                'bottom-mid': '',
                'bottom-left': '',
                'bottom-right': '',
                'left': '',
                'left-mid': '',
                'mid': '',
                'mid-mid': '',
                'right': '',
                'right-mid': '',
                'middle': ' '
            },
            colWidths,
            wordWrap: true,
            style: { 'padding-left': 0, 'padding-right': 0 },
        });

        const cols = JSON.parse(JSON.stringify(data)).map(col => {
            col.content = col.content.join('')

            col.hAlign = 'left'
            if(col.align === 'C') col.hAlign = 'center'
            else if(col.align === 'R') col.hAlign = 'right'
            delete col.align

            return col
        })

        let rows = []
        let curRow = []
        cols.forEach((col, index) => {
            if(col.content === 'divider') {
                if(index === 0) return

                rows.push([...curRow])
                rows.push([{ content: '-'.repeat(this.charsPerLine), hAlign: 'left', colSpan: colNumber }])
                curRow = []
                return
            }

            const cellsUsed = curRow.reduce((a, b) => a + (b.colSpan || 1), 0);
            if((cellsUsed + (col.colSpan || 1) <= colNumber)) {
                curRow.push(col)
            } else {
                rows.push([...curRow])
                curRow = [col]
            }

            if(index === cols.length - 1) {
                rows.push([...curRow])
            }
        })

        table.push(...rows);

        return table.toString().split('\n')
    }

    /**
     * Formats the line according to the alignment
     *
     * @param {string} content - Content of the line
     * @param {number} charsPerLine - The maximum line length
     * @returns {string[]} An array of lines
     */
    processLine(content, align = 'L') {
        let lines = [];

        // Break the content into chunks that fit within the charsPerLine limit
        let contentChunks = this.splitString(content);

        for (let i = 0; i < contentChunks.length; i++) {
            let contentChunk = contentChunks[i];
            let line = '';
            let leftPadding, rightPadding;

            switch (align) {
                case 'L':
                    leftPadding = '';
                    rightPadding = ' '.repeat(this.charsPerLine - contentChunk.length);
                    break;
                case 'R':
                    leftPadding = ' '.repeat(this.charsPerLine - contentChunk.length);
                    rightPadding = '';
                    break;
                case 'C':
                    leftPadding = ' '.repeat(Math.floor((this.charsPerLine - contentChunk.length) / 2));
                    rightPadding = ' '.repeat(this.charsPerLine - contentChunk.length - leftPadding.length);
                    break;
                default:
                    throw new Error(`Invalid alignment value: ${align}. Expected 'L', 'R', or 'C'.`);
            }

            line = leftPadding + contentChunk + rightPadding;

            lines.push(line);
        }

        return lines;
    }

    /**
     * Formats an array of two strings according to 'space-between' CSS rule
     *
     * @param {string[]} arr - Array of two strings
     * @returns {string[]} An array of lines
     */
    processSpaceBetween(arr) {
        let [leftString, rightString] = arr;
        let availableSpace = this.charsPerLine - leftString.length - rightString.length;

        // Both strings can fit on the same line with at least one space
        if (availableSpace >= 1) {
            let line = leftString + ' '.repeat(availableSpace) + rightString;
            return [this.padLine(line)];
        }

        // The strings can't fit on the same line, they need to be on separate lines
        let leftLines = this.splitString(leftString);
        let rightLines = this.splitString(rightString).map(line =>
            ' '.repeat(this.charsPerLine - line.length) + line
        );

        return [...leftLines, ...rightLines];
    }

    /**
     * Formats an array of two strings according to 'space-or-break' rule
     *
     * @param {string[]} arr - Array of two strings
     * @returns {string[]} An array of lines
     */
    processSpaceOrBreak(arr) {
        let [leftString, rightString] = arr;

        // Both strings can fit on the same line with a space
        if (leftString.length + rightString.length + 1 <= this.charsPerLine) {
            let line = leftString + ' ' + rightString;
            return [this.padLine(line)];
        }

        // The strings can't fit on the same line, they need to be on separate lines
        let leftLines = this.splitString(leftString).map(line => this.padLine(line));
        let rightLines = this.splitString(rightString).map(line => this.padLine(line));

        return [...leftLines, ...rightLines];
    }

    /**
     * Creates a divider line with text content
     *
     * @param {string} delimiter - The character used as a divider
     * @param {string} content - The text content
     * @param {string} align - The alignment of the text content ('L', 'R', or 'C')
     * @returns {string[]} An array of lines
     */
    processDivider(delimiter, content, align) {
        let lines = [];

        // Break the content into chunks that fit within the charsPerLine limit
        let contentChunks = this.splitString(content);

        for (let i = 0; i < contentChunks.length; i++) {
            let contentChunk = contentChunks[i];
            let line = '';
            let leftPadding, rightPadding;

            switch (align) {
                case 'L':
                    leftPadding = '';
                    rightPadding = ' '.repeat(this.charsPerLine - contentChunk.length);
                    break;
                case 'R':
                    leftPadding = ' '.repeat(this.charsPerLine - contentChunk.length);
                    rightPadding = '';
                    break;
                case 'C':
                    leftPadding = ' '.repeat(Math.floor((this.charsPerLine - contentChunk.length) / 2));
                    rightPadding = ' '.repeat(this.charsPerLine - contentChunk.length - leftPadding.length);
                    break;
                default:
                    throw new Error(`Invalid alignment value: ${align}. Expected 'L', 'R', or 'C'.`);
            }

            line = leftPadding + contentChunk + rightPadding;

            // Only the last line should be padded with the delimiter character
            if (i === contentChunks.length - 1) {
                line = line.replace(/^ +| +$/g, '').padStart(leftPadding.length + contentChunk.length, delimiter).padEnd(this.charsPerLine, delimiter);
            }

            lines.push(line);
        }

        return lines;
    }

    /**
     * Splits a string into lines of a given length, breaking at spaces where possible, and into individual characters where necessary
     *
     * @param {string} str - The string to be split
     * @returns {string[]} An array of lines
     */
    splitString(str) {
        let words = str.split(' ');
        let lines = [];
        let line = '';

        words.forEach(word => {
            if (word.length > this.charsPerLine) {
                // Word is too long, split it into segments
                let wordSegments = this.breakWord(word);

                // Add all full-length segments to lines array
                let fullLengthSegments = wordSegments.filter(segment => segment.length === this.charsPerLine);
                lines = [...lines, ...fullLengthSegments];

                // Last segment, if any, is shorter and should be added to the line
                let lastSegment = wordSegments[wordSegments.length - 1];
                if (lastSegment.length < this.charsPerLine) {
                    line += lastSegment + ' ';
                }

                return;
            }

            if ((line + word).length > this.charsPerLine) {
                // Word doesn't fit on the current line, start a new line
                lines.push(line.trim());
                line = '';
            }

            // Word fits on the current line
            line += word + ' ';
        });

        // Add the remaining line, if any
        if (line) lines.push(line.trim());

        return lines;
    }

    /**
     * Breaks a long word into segments
     *
     * @param {string} word - The word to be broken into segments
     * @returns {string[]} An array of word segments
     */
    breakWord(word) {
        let segments = [];

        while (word.length > this.charsPerLine) {
            let segment = word.substring(0, this.charsPerLine);
            segments.push(segment);
            word = word.substring(this.charsPerLine);
        }

        // If there's any remainder of the word, add it to the segments
        if (word) segments.push(word);

        return segments;
    }

    /**
     * Pads a line with spaces until it reaches a given length
     *
     * @param {string} line - The line to be padded
     * @returns {string} The padded line
     */
    padLine(line) {
        return line + ' '.repeat(this.charsPerLine - line.length);
    }
}
